import * as React from 'react';
import {
    List,
    EditButton,
    Datagrid,
    TextField,
    useRecordContext,
    TextInput,
    Filter,
    useNotify,
    FieldProps,
    useRefresh,
    TopToolbar,
    SelectInput,
    useListContext,
    Link,
    ShowButton,
} from 'react-admin';
import FormattedDateTimeField from '../../common/FormattedDateTimeField';
import Lottie from 'lottie-react';
import { Button, DialogTitle, IconButton, Typography } from '@mui/material';
import {
    Add,
    ChangeCircle,
    CheckBox,
    CopyAll,
    Schedule,
    Settings,
    SyncProblem,
    ViewCozy,
} from '@mui/icons-material';
import { Tests } from '../../custom';
import { useNavigate } from 'react-router-dom';
import lottie from '../../settings.json';
import { displayDuration } from '../../common/string-utilities';
import { BASE_URL, BASE_URL_GO, VIDEO_TYPES } from '../../common/constants';
import { PlayArrow } from '@mui/icons-material';
import { Dialog, DialogContent } from '@mui/material';
import VideoPlayerPage from '../../common/VideoPlayer/VideoPlayerPage';
import useIsPermission from '../../hooks/useIsPermission';
import ClearFiltersOnRouteChange from '../../common/ClearFiltersOnRouteChange';
import ConfirmDeleteButton from '../../common/ConfirmDeleteButton';

import { AutocompleteInput, useGetList } from 'react-admin';
import CloseIcon from '@mui/icons-material/Close';
import { goApi } from '../../services';

const VideoSubjectAutoComplete = (props: any) => {
    const { data, isLoading } = useGetList(
        'video-subjects',
        {
            pagination: { page: 1, perPage: 50 },
            sort: { field: 'created_at', order: 'DESC' },
            filter: { deleted: false },
        },
        {
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
        },
    );

    return (
        <AutocompleteInput
            {...props}
            source="videoSubjectId"
            label="Video Subject"
            choices={data || []}
            optionText="name"
            optionValue="id"
            loading={isLoading}
        />
    );
};

const VideoFilter = (props: any) => {
    return (
        <Filter {...props}>
            <TextInput
                variant={'outlined'}
                label="Title"
                source="title"
                alwaysOn={true}
                defaultValue=""
            />
            <TextInput
                variant={'outlined'}
                label="Code"
                source="code"
                defaultValue=""
                alwaysOn={true}
            />
            <VideoSubjectAutoComplete alwaysOn />
            <SelectInput
                source="type"
                label={'Type'}
                fullWidth
                alwaysOn={true}
                choices={VIDEO_TYPES}
            />
        </Filter>
    );
};

const CopyUrl = (props: any) => {
    const record = useRecordContext();
    return (
        <Button
            disabled={!record[props['source']]}
            startIcon={<CopyAll />}
            onClick={() => navigator.clipboard.writeText(record[props['source']])}
        >
            Copy
        </Button>
    );
};
const ProcessingStatus = (props: any) => {
    const record = useRecordContext();
    let icon = <SyncProblem color={'error'} />;
    const navigate = useNavigate();

    const updated_at = new Date(record['updated_at']).getTime();
    const now = new Date().getTime();
    const oneHourInMs = 60 * 60 * 1000;
    const isReprocessing = now - updated_at > oneHourInMs;

    if (record['status'] === 'PROCESSED' || record['type'] === 'YOUTUBE') {
        icon = <CheckBox color={'success'} />;
    }
    if (record['status'] === 'QUEUED') {
        icon = <Schedule color={'info'} />;
    }
    if (record['status'] === 'PROCESSING' && isReprocessing) {
        icon = <Schedule color={'info'} />;
    } else if (record['status'] === 'PROCESSING') {
        icon = (
            <div className={'w-8 h-8'}>
                <Lottie animationData={lottie} loop={true} />
            </div>
        );
    }
    return (
        <div className={'flex items-center space-x-2'}>
            {icon}
            <div className={'rounded bg-gray-500 text-white px-2 cursor-pointer hover:bg-gray-600'}>
                Logs
            </div>
        </div>
    );
};
export const VideoThumbnail = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const [open, setOpen] = React.useState(false);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <div className={'flex space-x-2 items-center'}>
            {((record.processedUrl && record.type == 'YOUTUBE') ||
                (record.processedUrl && !record.is_processing)) && (
                <ShowVideoIcon open={open} handleClose={handleClose} />
            )}
            <div>
                <div
                    className={
                        'min-h-[45px] min-w-[100px] aspect-video rounded overflow-hidden bg-gray-100 border bg-center bg-contain bg-no-repeat relative'
                    }
                    style={{ backgroundImage: `url(${record.thumbnail})` }}
                    onClick={handleClickOpen}
                >
                    {record.type !== 'YOUTUBE' && (
                        <div
                            className={
                                'p-2 text-center absolute -bottom-0 font-semibold bg-white px-1.5 py-0.5 rounded-md text-xs items-center flex space-x-0.5'
                            }
                        >
                            <Schedule style={{ fontSize: '12px' }} />{' '}
                            <div>{displayDuration(record.durationInSeconds)}</div>
                        </div>
                    )}
                    <div className={'absolute inset-0 flex items-center justify-center'}>
                        <PlayArrow
                            style={{ fontSize: '48px', color: 'rgba(255, 255, 255, 0.8)' }}
                        />
                    </div>
                </div>
            </div>
            <div className={'flex flex-col items-start'}>
                <div className={'font-medium'}>{record.title}</div>
                <div
                    className={
                        'text-xs flex space-x-2 items-center cursor-pointer rounded-md transition-all duration-200 hover:bg-indigo-50 px-2 py-1 mt-1'
                    }
                    onClick={() => {
                        navigator.clipboard.writeText(record.code);
                        notify('Code Copied', {
                            type: 'success',
                        });
                    }}
                >
                    <CopyAll fontSize={'small'} />
                    <div>{record.code}</div>
                </div>
            </div>
        </div>
    );
};
VideoThumbnail.defaultProps = {
    label: 'Video',
};
export const VideoQualitiesEncodings = () => {
    const record = useRecordContext();
    const qualities = record['qualities'] || [];
    const encodings = record['encodings'] || [];
    const drmEnabled = !!record['drm_enabled'];
    return (
        <div className={'flex flex-col '}>
            {qualities?.length > 0 ? (
                <div className={'flex space-x-1 items-center'}>
                    <Settings style={{ fontSize: '14px' }} />
                    {qualities.map((quality: string) => {
                        return (
                            <div
                                key={quality}
                                className={'text-xs px-1 py-0.5 rounded-md bg-gray-50'}
                            >
                                {quality}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <></>
            )}
            {encodings?.length > 0 ? (
                <div className={'flex space-x-1 items-center'}>
                    <ViewCozy style={{ fontSize: '14px' }} />
                    {encodings.map((encoding: string) => {
                        return (
                            <div
                                key={encoding}
                                className={'text-xs px-1 py-0.5 rounded-md bg-gray-50'}
                            >
                                {encoding}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <></>
            )}
            {
                <div className={'flex space-x-1 items-center'}>
                    {record.type === 'YOUTUBE' ? (
                        <div className={'text-xs px-1 py-0.5 rounded-md bg-red-500 text-white'}>
                            Youtube
                        </div>
                    ) : (
                        <>
                            {drmEnabled ? (
                                <div
                                    className={
                                        'text-xs px-1 py-0.5 rounded-md bg-blue-500 text-white'
                                    }
                                >
                                    DRM
                                </div>
                            ) : (
                                <>
                                    {record.type === 'SECURE' ? (
                                        <div className="text-xs px-1 py-0.5 rounded-md bg-amber-500 text-white">
                                            Secure
                                        </div>
                                    ) : record.type === 'SECURE_V2' ? (
                                        <div className="text-xs px-1 py-0.5 rounded-md bg-green-500 text-white">
                                            Secure V2
                                        </div>
                                    ) : (
                                        <div className="text-xs px-1 py-0.5 rounded-md bg-purple-500 text-white">
                                            Super Secure
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>
            }
        </div>
    );
};
VideoQualitiesEncodings.defaultProps = {
    label: 'Metadata',
};

const ReProcessIcon = () => {
    const record = useRecordContext();
    const [loading, setLoading] = React.useState(false);
    const refresh = useRefresh();
    const notify = useNotify();
    const updated_at = new Date(record['updatedAt']).getTime();
    const now = new Date().getTime();
    const oneHourInMs = 60 * 60 * 1000;
    const isReprocessing = now - updated_at > oneHourInMs;
    if (
        !(
            record['status'] === 'QUEUED' &&
            (record.type === 'CF_SECURE' || record.type === 'SECURE_V2')
        ) &&
        !(isReprocessing && record['status'] === 'PROCESSING')
    ) {
        return <></>;
    }
    return (
        <Button
            disabled={loading}
            onClick={async () => {
                try {
                    setLoading(true);

                    await goApi.post(`/video/process/${record.id}`);

                    refresh();
                } catch (error: any) {
                    console.error('API call failed:', error?.message || error);
                    notify(`Error: ${error?.message || 'Something went wrong'}`, {
                        type: 'warning',
                    });
                } finally {
                    setLoading(false);
                }
            }}
        >
            <ChangeCircle />
        </Button>
    );
};
const Actions = (props: FieldProps<Tests>) => {
    const { isUpdatable, isDeletable } = useIsPermission('VIDEO');
    return (
        <div className={'flex justify-end'}>
            {isUpdatable && (
                <>
                    <ReProcessIcon />
                    <EditButton />
                </>
            )}
            {isDeletable && <ConfirmDeleteButton />}
            <ShowButton />
        </div>
    );
};

const ShowVideoIcon = (props: {
    open: boolean;
    handleClose: ((event: {}, reason: 'backdropClick' | 'escapeKeyDown') => void) | undefined;
}) => {
    const record = useRecordContext();

    return (
        <Dialog open={props.open} onClose={props.handleClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ m: 0, p: 2 }}>
                {record.title}
                <IconButton
                    aria-label="close"
                    onClick={event => {
                        if (props.handleClose) {
                            props.handleClose(event as React.MouseEvent, 'escapeKeyDown');
                        }
                    }}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <VideoPlayerPage url={record.processedUrl} type={record.type} />
        </Dialog>
    );
};

Actions.defaultProps = {
    label: 'Actions',
};
const ListActions = () => {
    const { isWritable } = useIsPermission('VIDEO');

    const navigate = useNavigate();
    const { filterValues } = useListContext();

    const query = new URLSearchParams(filterValues).toString();

    return (
        <TopToolbar>
            {isWritable && (
                <>
                    <Link to={`/videos/create?${query}`} style={{ textDecoration: 'none' }}>
                        <Add /> <Typography variant="button">Create</Typography>
                    </Link>
                </>
            )}
        </TopToolbar>
    );
};

const VideoList = () => {
    return (
        <div>
            <List
                queryOptions={{
                    refetchOnReconnect: false,
                    refetchOnWindowFocus: false,
                }}
                actions={<ListActions />}
                sort={{ field: 'code', order: 'DESC' }}
                filters={<VideoFilter />}
            >
                <ClearFiltersOnRouteChange enableFilterCheck={true} />
                <Datagrid>
                    <VideoThumbnail />
                    <TextField source={'isDownloadable'} sortable={false} />
                    <VideoQualitiesEncodings />
                    <CopyUrl source={'processedUrl'} sortable={false} />

                    <TextField source="videoSubject.name" label="Video Subject" sortable={false} />
                    <ProcessingStatus source={'processing_status'} sortable={false} />
                    <FormattedDateTimeField source="createdAt" sortable={false} />
                    <Actions />
                </Datagrid>
            </List>
        </div>
    );
};
export default VideoList;
